import { HqApiService } from 'hq/services/hqApiService';
import { OgpApiService } from 'ogp/services/ogp-api-service';
import { Site } from 'shared/hooks';
import {
  Companies,
  companiesSchema,
  companyEmploymentTypesSchema,
  GetCompanyEmploymentTypes,
  GetContractorCompanyRates,
  getContractorCompanyRatesSchema,
  UpdateContractorCompanyRate,
} from './company-service.schema';
import {
  CompaniesDto,
  CompanyEmploymentTypesResponseDto,
  GetContractorCompanyRatesDto,
} from './company-service.dto';

type CompaniesProps = {
  site: Site;
  includeArchived: boolean;
  filter: string | undefined;
};

export class CompanyService {
  constructor(
    private readonly ogpApiService: OgpApiService,
    private readonly hqApiService: HqApiService
  ) {}

  public getCompanyEmploymentTypes = async (
    site: Site,
    companyId?: string
  ): Promise<GetCompanyEmploymentTypes> => {
    const companyEmploymentTypes = await this.getCompanyEmploymentTypesImpl(site, companyId);

    return companyEmploymentTypesSchema.parse(companyEmploymentTypes);
  };

  private async getCompanyEmploymentTypesImpl(
    site: Site,
    companyId?: string
  ): Promise<CompanyEmploymentTypesResponseDto> {
    if (site === 'hq') {
      return await this.hqApiService.get<CompanyEmploymentTypesResponseDto>({
        path: `companies/${companyId}/employment-types/`,
      });
    }

    if (site === 'ogp') {
      return await this.ogpApiService.get<CompanyEmploymentTypesResponseDto>({
        path: `companies/employment-types/`,
      });
    }

    throw new Error('Not implemented');
  }

  public async getCompanies(props: CompaniesProps): Promise<Companies> {
    const { site, includeArchived, filter } = props;
    const companies = await this.getCompaniesImpl({ site, includeArchived, filter });

    return companiesSchema.parse(companies);
  }

  private async getCompaniesImpl(props: CompaniesProps): Promise<CompaniesDto> {
    const { site, includeArchived, filter } = props;
    if (site === 'hq') {
      const companies = await this.hqApiService.get<CompaniesDto>({
        params: { includeArchived: includeArchived ?? false, filter },
        path: 'companies',
      });
      return companies;
    }
    throw new Error('Not implemented');
  }

  public async getContractorCompanyRates(): Promise<GetContractorCompanyRates> {
    const response = await this.ogpApiService.get<GetContractorCompanyRatesDto>({
      path: 'companies/employment-type-job-type-rates',
    });

    return getContractorCompanyRatesSchema.parse(response);
  }

  public async updateContractorCompanyRate(payload: UpdateContractorCompanyRate): Promise<void> {
    await this.ogpApiService.update<void>({
      path: 'companies/employment-type-job-type-rates',
      body: payload,
    });
  }
}
